import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { trackConversion } from "../lib/ads";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Mic, Send, Volume2, VolumeX, Loader2, Check, RotateCcw, Radio, Timer, Keyboard, Square } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { OralBoardPanel } from "../components/OralBoardPanel";
import { createAvatarProvider, pickPanelCast } from "../avatar/AvatarProvider";
import { setNavGuard, clearNavGuard } from "../lib/navGuard";
import { LeaveInterviewDialog } from "../components/interview/LeaveInterviewDialog";
import { analyzeAnswerAudio } from "../lib/audioAnalysis";
import { InterviewReport } from "../components/interview/InterviewReport";
import { InterviewResults, GradingState } from "../components/interview/InterviewResults";
import { StartScreen } from "../components/interview/StartScreen";
import { MicCheck } from "../components/interview/MicCheck";

// Voice completion tuning (configurable). Continue listening through short thinking pauses;
// only finalize after a longer stretch of silence.
const SILENCE_MS = 4500;      // continuous silence that ends an answer
const SPEECH_RMS = 0.018;     // volume above this counts as speaking
const MAX_ANSWER_MS = 120000; // hard safety cap

export default function Interview() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [interviewId, setInterviewId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [starting, setStarting] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [grading, setGrading] = useState(false);
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "results" ? "results" : "practice";
  const resultId = params.get("result");
  const setTab = (t) => setParams(t === "results" ? { tab: "results" } : {}, { replace: true });
  const [scores, setScores] = useState(null);
  const [voiceOn, setVoiceOn] = useState(true);
  const [recording, setRecording] = useState(false);
  const [demo, setDemo] = useState(false); // Free-first / referral board: same engine, limited report
  const [boardComplete, setBoardComplete] = useState(false);
  const [terminated, setTerminated] = useState(false);
  const [panelist, setPanelist] = useState(null);
  const [aiSpeaking, setAiSpeaking] = useState(false);
  const [handsFree, setHandsFree] = useState(true);
  const [showText, setShowText] = useState(false);
  const [progress, setProgress] = useState(null);
  const [answerTimer, setAnswerTimer] = useState(null); // seconds remaining (subtle, never cuts off)
  const [boardStart, setBoardStart] = useState(null); // ms timestamp; pacing benchmark (~20 min voice, ~25 min typed), never a cutoff
  const [boardElapsed, setBoardElapsed] = useState(0);
  useEffect(() => {
    if (!boardStart || boardComplete || scores) return undefined;
    const t = setInterval(() => setBoardElapsed(Math.floor((Date.now() - boardStart) / 1000)), 1000);
    return () => clearInterval(t);
  }, [boardStart, boardComplete, scores]);
  const [cast, setCast] = useState(() => pickPanelCast());
  const [history, setHistory] = useState([]);
  const [responseMode, setResponseMode] = useState("voice"); // voice | text
  const targetMin = responseMode === "text" ? 25 : 20; // typed answers take longer than spoken ones
  const [searchParams, setSearchParams] = useSearchParams();
  const [micCheck, setMicCheck] = useState(false);
  const [department, setDepartment] = useState(null); // {id,name,city,state} chosen for this board (optional)
  useEffect(() => {
    const did = searchParams.get("department");
    if (!did) return;
    api.get(`/departments/${did}`).then(({ data }) => { if (data.id) setDepartment({ id: data.id, name: data.name, city: data.city, state: data.state }); }).catch(() => {});
    setSearchParams({}, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [usage, setUsage] = useState(null);
  const loadUsage = () => api.get("/usage").then(({ data }) => setUsage(data)).catch(() => {});

  const startRecRef = useRef(null);
  const isPremium = !!user?.is_premium;
  const scrollRef = useRef(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const audioRef = useRef(null);
  const vadRef = useRef({});
  const timerRef = useRef(null);
  const pendingTimerRef = useRef(null);
  const gradePollRef = useRef(null);
  useEffect(() => () => { if (gradePollRef.current) clearTimeout(gradePollRef.current); }, []);
  const boardCompleteRef = useRef(false);
  const finishOnceRef = useRef(false);
  const finishFallbackRef = useRef(null);
  const providerRef = useRef(createAvatarProvider(process.env.REACT_APP_AVATAR_PROVIDER || "local"));

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  const speakGenRef = useRef(0);
  // Hard-stop all panel speech: current playback + anything still being fetched.
  const stopAudio = () => {
    speakGenRef.current += 1;
    const a = audioRef.current;
    if (a) { try { a.onended = null; a.pause(); a.removeAttribute("src"); a.load(); } catch {} }
    setAiSpeaking(false);
  };
  useEffect(() => () => { clearInterval(timerRef.current); clearTimeout(finishFallbackRef.current); providerRef.current?.stop?.(); stopAudio(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (user && !interviewId) api.get("/interview/history").then(({ data }) => setHistory(data.interviews || [])).catch(() => {});
    if (user && !interviewId) loadUsage();
  }, [user, interviewId]);

  const startAnswerTimer = (secs) => {
    clearInterval(timerRef.current);
    if (!secs) { setAnswerTimer(null); return; }
    setAnswerTimer(secs);
    timerRef.current = setInterval(() => {
      setAnswerTimer((s) => {
        if (s === null) return null;
        if (s <= 1) { clearInterval(timerRef.current); return 0; }
        return s - 1;
      });
    }, 1000);
  };
  const clearAnswerTimer = () => { clearInterval(timerRef.current); pendingTimerRef.current = null; setAnswerTimer(null); };
  // The timer is only ARMED when a timed question arrives; it STARTS when the panelist finishes reading it
  // (audio ended) or when the candidate begins answering (mic / barge-in / typing) — whichever comes first.
  const armAnswerTimer = (secs) => { clearAnswerTimer(); pendingTimerRef.current = secs || null; };
  const fireAnswerTimer = () => {
    if (pendingTimerRef.current) { const s = pendingTimerRef.current; pendingTimerRef.current = null; startAnswerTimer(s); }
  };

  // onDone (optional): used for the chair's welcome — runs when the line finishes instead of arming the answer flow.
  const speak = async (text, p, onDone) => {
    if (!voiceOn || !(isPremium || demo)) { onDone?.(); return; } // Free-first / referral boards: the panel speaks its questions too
    const gen = speakGenRef.current;
    try {
      const { data } = await api.post("/voice/tts", { text, voice: p?.voice || "onyx", speed: p?.speed || 1.0 });
      if (gen !== speakGenRef.current) return; // interview ended / reset while fetching
      if (audioRef.current) {
        const a = audioRef.current;
        a.onplay = () => setAiSpeaking(true);
        a.onpause = () => setAiSpeaking(false);
        a.onended = () => {
          setAiSpeaking(false);
          if (onDone) { onDone(); return; }
          fireAnswerTimer();
          if (handsFree && !demo && responseMode !== "text" && !boardCompleteRef.current) startRecRef.current && startRecRef.current();
        };
        a.src = `data:${data.mime};base64,${data.audio_base64}`;
        a.play().catch(() => onDone?.());
      }
    } catch { onDone?.(); }
  };

  const interrupt = () => {
    try { audioRef.current?.pause(); } catch {}
    setAiSpeaking(false);
    fireAnswerTimer();
    startRecording();
  };

  // An oral board is "active" from the first panel line until it's finished, graded or terminated.
  const boardActive = Boolean(interviewId) && !scores && !boardComplete && !terminated && !grading;
  const [leaveRequest, setLeaveRequest] = useState(null); // { proceed } while the Stay/Leave dialog is open
  useEffect(() => {
    if (!boardActive) { clearNavGuard(); return undefined; }
    const guard = (proceed) => setLeaveRequest({ proceed });
    setNavGuard(guard);
    const onUnload = (e) => { e.preventDefault(); e.returnValue = ""; };  // browser-native refresh/close warning
    window.addEventListener("beforeunload", onUnload);
    return () => { clearNavGuard(guard); window.removeEventListener("beforeunload", onUnload); };
  }, [boardActive]);
  const leaveInterview = () => { const go = leaveRequest?.proceed; setLeaveRequest(null); clearNavGuard(); stopAudio(); go?.(); };

  const reset = () => {
    stopAudio();
    setInterviewId(null); setMessages([]); setScores(null); setBoardComplete(false); setTerminated(false); setGrading(false); if (gradePollRef.current) clearTimeout(gradePollRef.current);
    setProgress(null); setShowText(false); boardCompleteRef.current = false; clearAnswerTimer(); setBoardStart(null); setBoardElapsed(0);
  };

  // Free order mirrors the server: free-first → referral Voice reward → streak → purchased. Typed (streak/purchased) boards skip the mic check.
  const beginStart = () => {
    const iv = usage?.interviews || {};
    const referralVoice = !isPremium && !iv.trial_available && (iv.referral_voice || 0) > 0 && responseMode !== "text";
    const typedEarned = !isPremium && !iv.trial_available && !referralVoice && ((iv.credits || 0) > 0 || (iv.purchased || 0) > 0);
    if (responseMode !== "text" && !typedEarned) setMicCheck(true); else start();
  };

  const start = async () => {
    if (starting) return; // one board per click, even on a double-tap
    setMicCheck(false);
    setStarting(true);
    boardCompleteRef.current = false;
    const newCast = pickPanelCast();
    setCast(newCast);
    providerRef.current?.init?.(newCast).catch(() => {});
    try {
      const { data } = await api.post("/interview/start", { response_mode: responseMode, department_id: department?.id || null });
      trackConversion("mock_interview_started"); // only after the start request succeeded
      setInterviewId(data.interview_id);
      setDemo(!!data.demo);
      if (data.response_mode) setResponseMode(data.response_mode); // server decides (earned → typed, referral → voice)
      setBoardComplete(false); setTerminated(false);
      setProgress(data.progress || null);
      setScores(null);
      const gen = speakGenRef.current;
      // Question 1 (and the board clock + answer timer) begin only after the chair's welcome has finished.
      const beginQuestions = () => {
        if (gen !== speakGenRef.current) return; // board was reset/left during the welcome
        setPanelist(data.panelist || null);
        setMessages((m) => [...m, { role: "assistant", content: data.message, panelist: data.panelist }]);
        setBoardStart(Date.now()); setBoardElapsed(0);
        armAnswerTimer(data.timer);
        speak(data.message, data.panelist);
      };
      if (data.welcome?.text) {
        setPanelist(data.welcome.panelist || null);
        setMessages([{ role: "assistant", content: data.welcome.text, panelist: data.welcome.panelist, welcome: true }]);
        speak(data.welcome.text, data.welcome.panelist, () => setTimeout(beginQuestions, 1800));
      } else {
        setMessages([]);
        beginQuestions();
      }
    } catch (e) {
      if (e.usageLimit) { loadUsage(); return; }
      toast.error("Could not start the interview.");
    } finally {
      setStarting(false);
    }
  };

  const send = async (text, delivery = null) => {
    const msg = (text ?? input).trim();
    if (!msg || busy || !interviewId) return;
    const source = delivery ? "voice" : "text";
    setInput("");
    clearAnswerTimer();
    setMessages((m) => [...m, { role: "user", content: msg, source }]);
    setBusy(true);
    try {
      const { data } = await api.post(`/interview/${interviewId}/respond`, { message: msg, delivery, source });
      setMessages((m) => [...m, { role: "assistant", content: data.message, panelist: data.panelist }]);
      setPanelist(data.panelist || null);
      if (data.progress) setProgress(data.progress);
      if (data.terminated) {
        setTerminated(true); setBoardComplete(true); boardCompleteRef.current = true;
        speak(data.message, data.panelist);
        return;
      }
      if (data.interview_complete) {
        setBoardComplete(true); boardCompleteRef.current = true;
        // Let the panel's closing line play to the end, then grade. Once-only guard + 90 s safety fallback so it never sticks.
        finishOnceRef.current = false;
        const finishOnce = () => { if (finishOnceRef.current) return; finishOnceRef.current = true; clearTimeout(finishFallbackRef.current); finish(); };
        finishFallbackRef.current = setTimeout(finishOnce, 90000);
        speak(data.message, data.panelist, finishOnce);
        return;
      }
      armAnswerTimer(data.timer);
      speak(data.message, data.panelist);
    } catch (e) {
      toast.error(e.response?.status === 400 ? "This board has already ended." : "Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const finish = async () => {
    if (!interviewId) return;
    stopAudio();
    setFinishing(true);
    try {
      const { data } = await api.post(`/interview/${interviewId}/finish`);
      if (data.scores) { setScores(data.scores); return; }
      setGrading(true);
      const id = interviewId;
      const poll = async () => {
        try {
          const r = await api.get(`/interview/${id}/result`);
          if (r.data.scores) { setScores(r.data.scores); setGrading(false); toast.success("Your report is ready."); return; }
        } catch { /* keep polling */ }
        gradePollRef.current = setTimeout(poll, 4000);
      };
      gradePollRef.current = setTimeout(poll, 4000);
    } catch {
      toast.error("Could not start grading. Your board is saved — open Results and press Retry grading.");
    } finally {
      setFinishing(false);
    }
  };

  const stopVad = () => {
    if (vadRef.current.raf) cancelAnimationFrame(vadRef.current.raf);
    try { vadRef.current.ac?.close(); } catch {}
    vadRef.current = {};
  };

  const startRecording = async () => {
    if (recording) return;
    fireAnswerTimer();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const preferred = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"].find((t) => window.MediaRecorder?.isTypeSupported?.(t));
      const mr = preferred ? new MediaRecorder(stream, { mimeType: preferred }) : new MediaRecorder(stream); // iOS Safari → audio/mp4
      chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stopVad();
        stream.getTracks().forEach((t) => t.stop());
        const mime = (mr.mimeType || "audio/webm").split(";")[0];
        const ext = mime.includes("mp4") ? "m4a" : mime.includes("ogg") ? "ogg" : mime.includes("mpeg") ? "mp3" : "webm";
        const blob = new Blob(chunksRef.current, { type: mime });
        if (blob.size < 1200) { setBusy(false); return; } // nothing captured
        const form = new FormData();
        form.append("audio", blob, `answer.${ext}`);
        setTranscribing(true);
        try {
          const [{ data }, metrics] = await Promise.all([
            api.post("/voice/transcribe", form, { headers: { "Content-Type": "multipart/form-data" } }),
            analyzeAnswerAudio(blob),
          ]);
          setTranscribing(false);
          if (data.text?.trim()) await send(data.text.trim(), metrics);
          else toast.error("Didn't catch that — try again.");
        } catch {
          setTranscribing(false);
          toast.error("Transcription failed.");
        }
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);

      // Voice activity detection: keep listening through short pauses; finalize after SILENCE_MS.
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ac = new AudioCtx();
      const src = ac.createMediaStreamSource(stream);
      const analyser = ac.createAnalyser();
      analyser.fftSize = 2048;
      src.connect(analyser);
      const buf = new Float32Array(analyser.fftSize);
      const started = Date.now();
      let hasSpoken = false;
      let lastSpeech = Date.now();
      const tick = () => {
        analyser.getFloatTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
        const rms = Math.sqrt(sum / buf.length);
        const now = Date.now();
        if (rms > SPEECH_RMS) { hasSpoken = true; lastSpeech = now; }
        const silenceFor = now - lastSpeech;
        if ((hasSpoken && silenceFor > SILENCE_MS) || (now - started > MAX_ANSWER_MS)) {
          stopRecording();
          return;
        }
        vadRef.current.raf = requestAnimationFrame(tick);
      };
      vadRef.current = { ac, raf: requestAnimationFrame(tick) };
    } catch {
      toast.error("Microphone access denied.");
    }
  };

  const stopRecording = () => {
    if (!mediaRef.current || mediaRef.current.state === "inactive") return;
    mediaRef.current.stop();
    setRecording(false);
  };

  startRecRef.current = startRecording;

  const status = transcribing ? "Transcribing your answer…" : busy ? "Panel is thinking…" : aiSpeaking ? `${panelist?.name || "Panel"} is speaking` : recording ? "Listening — pause to think, I'll wait" : boardComplete ? "Board complete" : "Your turn";
  const inputOpen = !scores && !boardComplete;
  const micAllowed = responseMode !== "text" && (isPremium || demo); // free-first / referral boards may speak; earned free boards are typed
  const textAllowed = responseMode !== "voice" || (!isPremium && !demo);
  const textVisible = textAllowed && (showText || responseMode === "text" || (!isPremium && !demo));

  // ---------- START ----------
  const Tabs = () => (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
      <div className="inline-flex rounded-full border border-slate-200 bg-white p-1" role="tablist" data-testid="interview-tabs">
        {[["practice", "Practice"], ["results", "Results"]].map(([k, l]) => <button key={k} role="tab" type="button" aria-selected={tab === k} onClick={() => setTab(k)} className={`px-4 py-1.5 text-sm font-semibold rounded-full ${tab === k ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"}`} data-testid={`interview-tab-${k}`}>{l}</button>)}
      </div>
    </div>
  );

  if (!interviewId && tab === "results") {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <Tabs />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="interview-results">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-1">Results</h1>
          <p className="text-sm text-slate-600 mb-5">Every graded board you've completed — score, panel feedback, delivery and pacing.</p>
          <InterviewResults initialId={resultId} onNew={() => setTab("practice")} />
        </div>
      </div>
    );
  }

  if (!interviewId) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <Tabs />
        <audio ref={audioRef} className="hidden" />
        <StartScreen isPremium={isPremium} cast={cast} provider={providerRef.current} starting={starting} onStart={beginStart}
          voiceOn={voiceOn} setVoiceOn={setVoiceOn} handsFree={handsFree} setHandsFree={setHandsFree} history={history}
          responseMode={responseMode} setResponseMode={setResponseMode} onUpgrade={() => navigate("/pricing")} usage={usage}
          department={department} setDepartment={setDepartment} />
        {micCheck && <MicCheck onReady={start} onClose={() => setMicCheck(false)} onTypeOnly={isPremium ? () => { setResponseMode("text"); setMicCheck(false); } : undefined} />}
      </div>
    );
  }

  // ---------- REPORT ----------
  if (scores) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <audio ref={audioRef} className="hidden" />
        <InterviewReport scores={scores} messages={messages} onNew={reset} />
      </div>
    );
  }

  // ---------- ACTIVE BOARD ----------
  return (
    <div className="min-h-screen bg-slate-950 flex flex-col">
      <Navbar />
      <LeaveInterviewDialog open={Boolean(leaveRequest)} onStay={() => setLeaveRequest(null)} onLeave={leaveInterview} />
      <audio ref={audioRef} className="hidden" />
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 lg:h-[calc(100vh-4rem)] lg:overflow-hidden">
        {/* THE STAGE */}
        <div className="lg:col-span-8 bg-slate-950 text-slate-50 relative flex flex-col px-3 sm:px-6 lg:px-10 pt-3 pb-4 lg:pt-5 lg:pb-6 tactical-grid">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400">{demo ? "Free oral board" : "Oral board"}</span>
              {progress && (
                <span className="font-heading text-lg text-white" data-testid="question-progress">Question {Math.min(progress.asked, progress.total)} <span className="text-slate-500">of {progress.total}</span></span>
              )}
              {boardStart && (
                <span className={`hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold tabular-nums rounded-full px-2.5 py-1 border ${boardElapsed > targetMin * 60 ? "border-amber-500/40 text-amber-300" : "border-slate-700 text-slate-300"}`} title={`${responseMode === "text" ? "Typed boards typically run about 25 minutes" : "Boards typically run about 20 minutes"}. This is a pacing guide, not a limit — the panel finishes its questions either way.`} data-testid="board-timer">
                  <Timer className="w-3.5 h-3.5" /> {Math.floor(boardElapsed / 60)}:{String(boardElapsed % 60).padStart(2, "0")} <span className="text-slate-500 font-normal">/ ~{targetMin} min</span>
                </span>
              )}
            </div>
            {isPremium && (
              <div className="flex items-center gap-1" data-testid="board-settings">
                {responseMode !== "text" && <Button variant="ghost" size="sm" onClick={() => setHandsFree(!handsFree)} title="Hands-free auto-record" data-testid="handsfree-toggle" className="text-slate-300 hover:text-white hover:bg-white/10">
                  <Radio className={`w-4 h-4 ${handsFree ? "text-amber-400" : "text-slate-500"}`} />
                </Button>}
                <Button variant="ghost" size="sm" onClick={() => setVoiceOn(!voiceOn)} data-testid="toggle-voice-button" className="text-slate-300 hover:text-white hover:bg-white/10">
                  {voiceOn ? <Volume2 className="w-4 h-4 text-amber-400" /> : <VolumeX className="w-4 h-4 text-slate-500" />}
                </Button>
              </div>
            )}
          </div>

          {/* Subtle ~90s target timer (never cuts the candidate off) */}
          {(answerTimer !== null || pendingTimerRef.current) && inputOpen && (
            <div className="mt-3" data-testid="answer-timer">
              <div className="flex items-center justify-between text-xs text-slate-400 mb-1">
                <span className="inline-flex items-center gap-1"><Timer className="w-3.5 h-3.5" /> {answerTimer === null ? "About 90 seconds — starts when you begin" : "Target: about 90 seconds"}</span>
                <span className={`font-heading font-bold ${answerTimer === 0 ? "text-amber-400" : "text-white"}`}>
                  {answerTimer === null ? "1:30" : answerTimer === 0 ? "Take your time" : `${Math.floor(answerTimer / 60)}:${String(answerTimer % 60).padStart(2, "0")}`}
                </span>
              </div>
              <div className="h-1 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full bg-amber-500 transition-[width] duration-1000" style={{ width: `${Math.max(0, ((answerTimer ?? 90) / 90) * 100)}%` }} />
              </div>
            </div>
          )}

          <div className="flex-1 flex items-center justify-center mt-4 min-h-0">
            <div className="w-full" style={{ maxWidth: "min(100%, calc((100vh - 21rem) * 16 / 7))" }}>
              <OralBoardPanel activeName={panelist?.name} speaking={aiSpeaking} listening={recording} panelists={cast} provider={providerRef.current} caption="AI panelists · animated portraits" />
            </div>
          </div>

          {/* Current line from the panel */}
          <div className="mt-3 lg:mt-4 min-h-[3.5rem] lg:min-h-[4.5rem]">
            <AnimatePresence mode="wait">
              {messages.length > 0 && messages[messages.length - 1].role === "assistant" && (
                <motion.div key={messages.length} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="max-w-3xl" data-testid="current-panel-line">
                  <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-amber-400" data-testid="current-panelist-name">{panelist?.name || "Panel"}<span className="text-slate-500 normal-case tracking-normal font-normal ml-2">{panelist?.role}{panelist?.style ? ` · ${panelist.style}` : ""}</span></p>
                  <p className="mt-1.5 text-base sm:text-xl text-slate-100 leading-snug">{messages[messages.length - 1].content}</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {aiSpeaking && micAllowed && inputOpen && (
            <Button onClick={interrupt} className="absolute right-4 sm:right-6 lg:right-10 bottom-6 bg-amber-500 hover:bg-amber-400 text-slate-950 font-heading tracking-wide shadow-lg" data-testid="barge-in-button" title="Cut in and answer now">
              <Mic className="w-4 h-4 mr-2" /> Cut in
            </Button>
          )}
        </div>

        {/* CONTROL CENTER */}
        <div className="lg:col-span-4 bg-white lg:border-l border-slate-200 flex flex-col lg:h-full">
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-3 text-sm max-h-[34vh] lg:max-h-none" data-testid="interview-transcript">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">Transcript</p>
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`interview-message-${i}`}>
                <div className="max-w-[88%]">
                  {m.role === "assistant" && m.panelist && <p className="text-[11px] font-semibold text-slate-400 mb-0.5 ml-1">{m.panelist.name}</p>}
                  <div className={`px-3.5 py-2.5 rounded-xl leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-slate-900 text-white rounded-tr-sm" : "bg-slate-100 text-slate-800 rounded-tl-sm"}`}>{m.content}</div>
                </div>
              </div>
            ))}
            {(busy || transcribing) && <div className="flex justify-start"><div className="bg-slate-100 px-3.5 py-2.5 rounded-xl rounded-tl-sm"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div></div>}
          </div>

          <div className="p-4 sm:p-5 border-t border-slate-100 bg-slate-50 flex flex-col gap-3 sm:gap-4 sticky bottom-0 lg:static pb-[max(1rem,env(safe-area-inset-bottom))]">
            {terminated ? (
              <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-xl border border-red-200 p-5" data-testid="interview-terminated-panel">
                <span className="inline-block text-[11px] font-bold uppercase tracking-widest text-red-600 bg-red-500/10 px-2.5 py-1 rounded-full">Interview ended</span>
                <h3 className="font-heading text-xl text-slate-900 mt-3">Removed for Conduct</h3>
                <p className="text-sm text-slate-600 mt-2">The panel ended this board early because of your conduct. Professionalism under pressure is part of the evaluation — even when you're challenged.</p>
                <Button onClick={reset} className="w-full mt-4 bg-primary hover:bg-primary/90" data-testid="start-over-button"><RotateCcw className="w-4 h-4 mr-2" /> Start Over</Button>
              </motion.div>
            ) : boardComplete ? (
              grading ? <GradingState compact onResults={() => { reset(); setTab("results"); }} /> : (
              <div className="flex items-center gap-3 text-sm text-slate-600 py-2" data-testid="deliberating">
                <Loader2 className="w-4 h-4 animate-spin" /> {finishing ? "The panel is wrapping up…" : "Preparing your report…"}
              </div>)
            ) : (
              <>
                <p className={`text-xs font-semibold uppercase tracking-wider ${recording ? "text-primary animate-pulse" : "text-slate-500"}`} data-testid="board-status">{status}</p>
                <div className="flex items-center gap-4">
                  {micAllowed ? (recording ? (
                    <button onClick={stopRecording} className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-green-600 hover:bg-green-700 text-white flex items-center justify-center shadow-lg transition-colors" data-testid="im-done-button" title="I'm done answering">
                      <span className="absolute inset-0 rounded-full bg-green-500 animate-pulse-ring" />
                      <Check className="w-8 h-8 relative" />
                    </button>
                  ) : (
                    <button onClick={aiSpeaking ? interrupt : startRecording} disabled={busy || transcribing}
                      className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center shadow-lg transition-colors disabled:opacity-40 ${aiSpeaking ? "bg-amber-500 hover:bg-amber-400 text-slate-950" : "bg-primary hover:bg-primary/90 text-white"}`}
                      data-testid="record-answer-button" title={aiSpeaking ? "Cut in and answer" : "Answer by voice"}>
                      <Mic className="w-8 h-8" />
                    </button>
                  )) : responseMode === "text" && (isPremium || demo) ? (
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-slate-900 text-white flex items-center justify-center shrink-0" title="Typed board" data-testid="typed-mode-badge"><Keyboard className="w-8 h-8" /></div>
                  ) : (
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-slate-200 text-slate-400 flex items-center justify-center" title="Voice answers are Pro"><Mic className="w-8 h-8" /></div>
                  )}
                  <div className="flex-1 text-sm text-slate-600">
                    {recording ? <>Speaking… tap <span className="font-semibold text-slate-900">Done</span> or just pause when you're finished.</>
                      : !isPremium ? (demo ? (responseMode === "text" ? <>Your free board — type your answer below. Content is graded; delivery isn't.</> : <>Tap the mic and answer out loud — the panel is listening.</>) : <>Your earned graded board — type your answer below. Content is graded; delivery isn't.</>)
                      : responseMode === "text" ? <>Typed board — write your answer below. Content is graded; delivery isn't.</>
                      : responseMode === "voice" ? <>Voice-only board — answer out loud, like a real panel.</>
                      : <>Tap the mic and answer out loud, like a real board.</>}
                  </div>
                </div>
                {textVisible && (
                  <div className="flex items-end gap-2">
                    <Textarea value={input}
                      onChange={(e) => { setInput(e.target.value); if (e.target.value.trim()) fireAnswerTimer(); }}
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                      placeholder="Type your answer…" rows={2} disabled={recording || busy} className="resize-none bg-white" data-testid="interview-input" />
                    <Button onClick={() => send()} disabled={busy || recording || !input.trim()} className="bg-slate-900 hover:bg-slate-800 h-10" data-testid="send-answer-button"><Send className="w-4 h-4" /></Button>
                  </div>
                )}
                {recording && <Button variant="ghost" size="sm" onClick={stopRecording} className="self-start text-slate-500" data-testid="stop-recording-secondary"><Square className="w-3.5 h-3.5 mr-1.5" /> Stop</Button>}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
